/*
 * Copyright (c) Worldline 2023.
 */

import {Injectable} from '@angular/core';
import {SharedService} from "./shared.service";
import {Errors} from "../constant/errors";
import {ExternalEvidenceTempDataStatus} from "../constant/external-evidence-temp-data-status";
import {ExternalEvidenceTempDataErrorCode} from "../constant/external-evidence-temp-data-error-code";
import {HttpService} from "../http/http.service";
import {TempDataAffectationDto} from "../model/tempDataAffectationDto";

@Injectable({
    providedIn: 'root'
})
export class EvidenceService {

    constructor(private sharedService: SharedService, private http: HttpService) {
    }

    //Upload success callback
    onUploadSuccess(response, sessionId, signerId, evidenceId, evidencePartId) {

        console.debug('ES - onUploadSuccess -> sessionId : [' + sessionId + '], signerId : [' + signerId + '], evidenceId : [' + evidenceId + '], evidencePartId : [' + evidencePartId + ']');

        var evidenceMsg: any;

        //text/plain to json; IE8 fix
        if (typeof response === 'string') {
            response = JSON.parse(response.replace(/<(\/)*pre>/gi, ''));
        }

        if (response.success) {
            console.debug('Start upload process');

            var tempData = {
                evidenceTempData: response.result
            };
            this.sharedService.broadcast('updateTempData', tempData);
            evidenceMsg = this.processTemporaryResponse(response.result, sessionId, signerId, evidenceId);
        } else {
            console.warn(response.error.msg);

            switch (response.error.errorCode) {
                case Errors.storageError :
                    evidenceMsg = 'error.evidences.upload';
                    break;
                case Errors.evidenceWrongMimeType:
                    evidenceMsg = 'evidences.error.wrong-mime-type';
                    break;
                case Errors.evidenceIncorrectCroppingData:
                    evidenceMsg = 'error.evidences.cropping.data.update';
                    break;
                case Errors.tooBigFile:
                    evidenceMsg = 'evidences.error.file-too-large';
                    break;
                default:
                    evidenceMsg = 'error.description';
                    break;
            }

            var data = {
                evidenceId: evidenceId,
                evidencePartId: evidencePartId
            };

            this.sharedService.broadcast('uploadFailed', data);

        }

        return evidenceMsg;
    };

    //Get evidence part
    getEvidencePart(sessionId, signerId, evidenceId, evidencePartId) {
        const that = this;

        console.debug('ES - getEvidencePart -> sessionId : [' + sessionId + '], signerId : [' + signerId + '], evidenceId : [' + evidenceId + '], evidencePartId : [' + evidencePartId + ']');

        var evidenceMsg: any;

        this.http.getEvidencePart(sessionId, signerId, evidenceId, evidencePartId)
            .toPromise()
            .then(function (response) {
                if (response?.success) {
                    console.debug('Upload of an evidence part');

                    var result = response.result as any;

                    //Send event to update displaying in part scope

                    that.sharedService.broadcast('partUploaded', {
                        evidenceId: evidenceId,
                        partId: evidencePartId
                    });


                    //Send data to parent scope for part update
                    var data = {
                        evidenceId: evidenceId,
                        part: result
                    };
                    that.sharedService.broadcast('updatePart', data);

                } else {
                    console.warn(response?.error?.msg);
                    evidenceMsg = 'error when getting evidence part from temporary';
                }
            }, function (error) {
                console.warn(error);
                evidenceMsg = 'error when getting evidence part from temporary';
            });

        return evidenceMsg;
    };


    checkLoop(sessionId, signerId, evidenceId, extEvidencePartId) {
        const that = this;
        var evidenceMsg = null;

        //Trigger a checking loop
        setTimeout(function () {
            evidenceMsg = that.checkUpload(sessionId, signerId, evidenceId);
        }, 5000);

        var data = {
            evidenceId: evidenceId,
            evidencePartId: extEvidencePartId
        };

        //Event for part loading
        this.sharedService.broadcast('partUploadInProgress', data);

        return evidenceMsg;
    };

    //Check upload function
    checkUpload(sessionId, signerId, evidenceId) {
        const that = this;

        console.debug('ES - checkUpload -> sessionId : [' + sessionId + '], signerId : [' + signerId + '], evidenceId : [' + evidenceId + ']');

        var evidenceMsg: any;

        this.http.checkUpload(sessionId, signerId, evidenceId)
            .toPromise()
            .then(function (response) {
                if (response?.success) {
                    console.debug('Check upload done');

                    //Update temporary response on the evidence scope
                    var data = {
                        evidenceTempData: response.result
                    };
                    that.sharedService.broadcast('updateTempData', data);

                    that.processTemporaryResponse(response.result, sessionId, signerId, evidenceId);
                } else {
                    console.warn(response?.error?.msg);
                    evidenceMsg = 'error when checking';
                }
            }, function (error) {
                console.warn(error);
                evidenceMsg = 'error when checking';
            });

        return evidenceMsg;
    }

    //Process temporary response
    processTemporaryResponse(temporaryResponse, sessionId, signerId, evidenceId) {

        console.debug('ES - processTemporaryResponse -> sessionId : [' + sessionId + '], signerId : [' + signerId + '], evidenceId : [' + evidenceId + '], evidencePartId : [' + temporaryResponse.extEvidencePartId + ']');

        var evidenceMsg: any;

        var data: any;

        switch (temporaryResponse.externalEvidenceTempDataStatus) {
            case ExternalEvidenceTempDataStatus.AFFECTED :
                console.debug('Evidence part ready');
                //Get part
                this.getEvidencePart(sessionId, signerId, evidenceId, temporaryResponse.extEvidencePartId);
                break;
            case ExternalEvidenceTempDataStatus.PROCESSING :
                console.debug('Temporary evidence part - the part is not ready (wait 5s)');
                //Loop to check upload and activate loading button
                evidenceMsg = this.checkLoop(sessionId, signerId, evidenceId, temporaryResponse.extEvidencePartId);
                break;
            case ExternalEvidenceTempDataStatus.AFFECTATION :
            case ExternalEvidenceTempDataStatus.TO_CROP_AND_AFFECTATION:
            case ExternalEvidenceTempDataStatus.TO_CROP:
                console.debug('Temporary evidence part - need cropping or dispatch');

                data = {
                    evidenceId: evidenceId,
                    evidencePartId: temporaryResponse.extEvidencePartId,
                    nbPages: temporaryResponse.nbPages
                };

                //Event for part loading
                this.sharedService.broadcast('temporaryToProcess', data);

                break;
            case ExternalEvidenceTempDataStatus.REJECT :
                console.debug('Temporary evidence part rejected');

                switch (temporaryResponse.externalEvidenceTempDataErrCode) {
                    case ExternalEvidenceTempDataErrorCode.TOO_BIG :
                        evidenceMsg = 'evidences.error.file-too-large';
                        break;
                    case ExternalEvidenceTempDataErrorCode.TOO_MANY_PAGES :
                        evidenceMsg = 'evidences.error.too-many-pages';
                        break;
                    case ExternalEvidenceTempDataErrorCode.ACCEPT_ONLY_ONE_PAGE_PDF :
                        evidenceMsg = 'evidences.error.only-one-page';
                        break;
                    case ExternalEvidenceTempDataErrorCode.CORRUPT_FILE :
                        evidenceMsg = 'evidences.error.file-corrupted';
                        break;
                    case ExternalEvidenceTempDataErrorCode.BAD_FORMAT :
                        evidenceMsg = 'evidences.error.file-bad-format';
                        break;
                    case ExternalEvidenceTempDataErrorCode.PROCESS_ERROR :
                        evidenceMsg = 'evidences.error.process-error';
                        break;
                }

                data = {
                    evidenceId: evidenceId,
                    evidencePartId: temporaryResponse.extEvidencePartId
                };

                this.sharedService.broadcast('uploadFailed', data);

                break;
        }

        return evidenceMsg;
    }

    //Delete evidence part
    deleteEvidence(sessionId, signerId, evidenceId, evidencePartId) {
        const that = this;

        console.debug('ES - deleteEvidence -> sessionId : [' + sessionId + '], signerId : [' + signerId + '], evidenceId : [' + evidenceId + '], evidencePartId : [' + evidencePartId + ']');

        var evidenceMsg: any;

        this.http.removePart(sessionId, signerId, evidenceId, evidencePartId).toPromise().then(function (response) {
            if (response?.success) {

                var data = {
                    evidenceId: evidenceId,
                    parts: response.result
                };

                that.sharedService.broadcast('deletePart', data);

            } else {
                console.warn(response?.error?.msg);
                evidenceMsg = 'error.evidences.delete';
            }
        }, function (error) {
            console.warn('An error occured when deleting an evidence');
            console.warn(error);
            evidenceMsg = 'error.evidences.delete';
        });

        return evidenceMsg;

    };

    //Verify evidence
    verifyEvidence(sessionId, signerId, evidenceId, evidencePartId, nbPages) {

        console.debug('ES - verifyEvidence -> sessionId : [' + sessionId + '], signerId : [' + signerId + '], evidenceId : [' + evidenceId + '], evidencePartId : [' + evidencePartId + '], temp total pages : [' + nbPages + ']');

        var data = {
            sessionId: sessionId,
            signerId: signerId,
            evidenceId: evidenceId,
            evidencePartId: evidencePartId,
            nbPages: nbPages
        };

        this.sharedService.broadcast('evidenceVerification', data);

    };

    affectTempData(sessionId, signerId, evidenceId, evidencePartId, croppingData, page) {
        const that = this;

        console.debug('ES - affectTempData -> sessionId : [' + sessionId + '], signerId : [' + signerId + '], evidenceId : [' + evidenceId + '], evidencePartId : [' + evidencePartId + '], temp page : [' + page + ']');

        var croppingMsg: any;

        var tempDataAffectationDto: TempDataAffectationDto = {
            croppingDataDto: croppingData,
            pageNumber: page,
        };

        return this.http.affectTempDataPageToPart(sessionId, signerId, evidenceId, evidencePartId, tempDataAffectationDto).toPromise().then(
            function (response) {
                if (response?.success) {
                    // Update the evidence
                    console.debug('affect success, need to update part');

                    //Emmit event for part affected
                    var data = {
                        evidenceId: evidenceId,
                        part: response.result
                    };
                    that.sharedService.broadcast('partAffected', data);

                    return croppingMsg;
                } else {
                    console.warn(response?.error?.msg);

                    //Return the error
                    switch (response?.error?.errorCode) {
                        case Errors.invalidAction :
                            croppingMsg = 'cropping.error.invalid-action';
                            break;
                    }

                    return croppingMsg;
                }
            }, function (error) {
                console.warn(error);
                croppingMsg = 'error.evidences.cropping.data.update';
                return croppingMsg;
            });
    };

}
